<template>
  <!-- Loader -->
  <div
    v-if="showLoader"
    class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
      class="animate-spin h-16 w-16 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
        fill="none"
      />
    </svg>
  </div>

  <div v-else class="p-6">
    <!-- Header with Back Button and Department Name -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center space-x-4">
        <button
          @click="goBack"
          class="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            class="w-6 h-6 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h2 class="text-2xl font-bold text-gray-800">
          {{ disciplineLabel }} Services
        </h2>
      </div>

      <div v-if="activeTab === 'services'" class="flex items-center space-x-4">
        <CustomButton
          label="Upload Services"
          :icon-path="'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'"
          bg-class="bg-blue-600"
          @click="showUploadServicesModal = true"
        />
        <CustomButton
          label="Add Service"
          :icon-path="'M5 12h14m-7 7V5'"
          bg-class="bg-custom-yellow"
          @click="showAddServicesModal = true"
        />
      </div>
    </div>

    <!-- Radiology department tabs: Services / Report Templates -->
    <div v-if="isRadiologyDepartment" class="tabs flex space-x-4 border-b mb-6">
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2 transition-colors"
        :class="activeTab === 'services' ? 'border-custom-yellow text-custom-blue' : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeTab = 'services'"
      >
        Services
      </button>
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2 transition-colors"
        :class="activeTab === 'report-templates' ? 'border-custom-yellow text-custom-blue' : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeTab = 'report-templates'"
      >
        Report Templates
      </button>
    </div>

    <!-- Report Templates panel -->
    <RadiologyReportTemplatesPage v-if="isRadiologyDepartment && activeTab === 'report-templates'" />

    <template v-else>
    <!-- Search Bar -->
    <div class="flex items-center space-x-4 mb-4">
      <div class="relative flex">
        <input
          type="text"
          v-model="searchQuery"
          class="px-4 py-2 text-gray-900 border border-gray-300 rounded-sm"
          placeholder="Search Service"
        />
        <button
          @click="triggerSearch"
          class="bg-custom-yellow text-white rounded-r px-2 md:px-3 py-0 md:py-1"
        >
          <svg
            class="w-6 h-6 text-gray-100"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="2"
              d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Services Table -->
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm">
      <table class="w-full text-left text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-6 py-4">No.</th>
            <th class="px-6 py-4">Service Name</th>
            <th class="px-6 py-4">Cost (KSH)</th>
            <th class="px-6 py-4">Created At</th>
            <th class="px-6 py-4">Updated At</th>  <!-- ← ADDED -->
            <th class="px-6 py-4">Action</th>
            <th class="px-6 py-4"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(service, index) in services"
            :key="service.id"
            class="border-b"
          >
            <td class="px-6 py-2">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
            <td class="px-6 py-2">{{ service.name }}</td>
            <td class="px-6 py-2">{{ service.unit_price }}</td>
            <td class="px-6 py-2">{{ formatDate(service.created_at) }}</td>
            <td class="px-6 py-2">{{ formatDate(service.updated_at) }}</td>  <!-- ← ADDED -->
            <td v-if="isLabDepartment" class="px-6 py-2">
                <button
                  @click="selectedService = service; showAssignAnalytesModal = true"
                  class="bg-green-600 text-white px-4 py-1 rounded flex items-center gap-2"
                >
                  Analytes
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                  </svg>
                </button>
            </td>
            <td class="px-6 py-2">
              <button
                @click="handleEditModal(service)"
                class="bg-custom-blue text-white px-4 py-1 rounded flex items-center gap-2"
              >
                Update
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="yellow"
                >
                  <path
                    d="M 7.1601562 3 L 8.7617188 5 L 18 5 C 18.551 5 19 5.448 19 6 L 19 15 L 16 15 L 20 20 L 24 15 L 21 15 L 21 6 C 21 4.346 19.654 3 18 3 L 7.1601562 3 z M 4 4 L 0 9 L 3 9 L 3 18 C 3 19.654 4.346 21 6 21 L 16.839844 21 L 15.238281 19 L 6 19 C 5.449 19 5 18.552 5 18 L 5 9 L 8 9 L 4 4 z"
                  ></path>
                </svg>
              </button>
            </td>
            <td class="px-6 py-2">
              <button
                @click="confirmDelete(service)"
                class="bg-red-600 text-white px-4 py-1 rounded flex items-center gap-2"
              >
                Delete
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M 10 2 L 9 3 L 4 3 L 4 5 L 5 5 L 5 20 C 5 20.522222 5.1913289 21.05461 5.5683594 21.431641 C 5.9453899 21.808671 6.4777778 22 7 22 L 17 22 C 17.522222 22 18.05461 21.808671 18.431641 21.431641 C 18.808671 21.05461 19 20.522222 19 20 L 19 5 L 20 5 L 20 3 L 15 3 L 14 2 L 10 2 z M 7 5 L 17 5 L 17 20 L 7 20 L 7 5 z M 9 7 L 9 18 L 11 18 L 11 7 L 9 7 z M 13 7 L 13 18 L 15 18 L 15 7 L 13 7 z"
                  ></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />
    </template>

    <AddServicesModal
      v-model:show="showAddServicesModal"
      :departmentId="departmentId"
      :departmentName="departmentName"
      :discipline="discipline"
      @close="showAddServicesModal = false"
      @refetch="fetchServices"
    />

    <UploadServicesModal
      v-model:show="showUploadServicesModal"
      :departmentId="departmentId"
      :departmentName="departmentName"
      :discipline="discipline"
      @close="showUploadServicesModal = false"
      @refetch="fetchServices"
    />

    <!-- Edit Service Modal -->
    <EditFacilityServiceModal
      v-model:service="selectedService"
      v-model:show="showEditFacilityServiceModal"
      v-model:serviceTypes="serviceTypes"
      @close="closeEditModal"
      @refetch="fetchServices"
    />

    <AssignAnalytesModal
      :show="showAssignAnalytesModal"
      :service="selectedService"
      @close="showAssignAnalytesModal = false"
      @saved="fetchServices"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      :show="showDeleteConfirmationModal"
      :showLoader="showDeleteLoader"
      title="Delete Confirmation"
      message="Are you sure you want to delete this service?"
      @cancel="closeConfirmationModal"
      @confirm="deleteFacilityService"
    />
  </div>
</template>

<script>
import apiClient from "@/services/api.js"
import CustomButton from "@/components/CustomButton.vue"
import PaginationComponent from "@/components/shared/pagination.vue"
import AddServicesModal from "@/views/modals/AddServicesModal.vue"
import UploadServicesModal from "@/views/modals/UploadServicesModal.vue"
import EditFacilityServiceModal from "@/views/modals/EditFacilityServiceModal.vue"
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue"
import AssignAnalytesModal from '@/views/modals/AssignAnalytesModal.vue'
import RadiologyReportTemplatesPage from '@/views/pages/RadiologyReportTemplatesPage.vue'

export default {
  name: 'DepartmentServices',
  components: {
    CustomButton,
    PaginationComponent,
    AddServicesModal,
    UploadServicesModal,
    EditFacilityServiceModal,
    ConfirmationModal,
    AssignAnalytesModal,
    RadiologyReportTemplatesPage
  },
  data() {
    return {
      showLoader: false,
      departmentId: null,
      departmentName: "",
      activeTab: 'services',
      services: [],
      serviceTypes: [],
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: null,
      totalItems: null,
      showAddServicesModal: false,
      showUploadServicesModal: false,
      showEditFacilityServiceModal: false,
      showDeleteConfirmationModal: false,
      showDeleteLoader: false,
      selectedService: null,
      itemToDelete: null,
      showAssignAnalytesModal: false,
      discipline: null
    }
  },
  computed: {
    isLabDepartment() {
      return this.departmentName?.toLowerCase().includes('lab')
    },
    isRadiologyDepartment() {
      return this.departmentName?.toLowerCase().includes('radiology')
    },
    disciplineLabel() {
      if (!this.discipline || this.discipline === '__unclassified__') return this.departmentName
      const labels = {
        haematology: 'Haematology', biochemistry: 'Clinical Chemistry',
        immunoassay: 'Immunoassay', coagulation: 'Coagulation',
        serology: 'Serology', hormones: 'Hormones',
        tumour_markers: 'Tumour Markers', microbiology: 'Microbiology',
        urinalysis: 'Urinalysis', stool_parasitology: 'Stool & Parasitology',
        csf_analysis: 'CSF Analysis', semen_analysis: 'Semen Analysis',
      }
      return labels[this.discipline] || this.discipline.replace(/_/g, ' ')
    },
  },
  methods: {
    async fetchServices() {
      try {
        this.showLoader = true
        let response

        if (this.discipline) {
          response = await apiClient.getServicesByDiscipline({
            department_id: this.departmentId,
            discipline:    this.discipline === '__unclassified__' ? '' : this.discipline,
            page:          this.currentPage,
            per_page:      this.itemsPerPage
          })
        } else {
          response = await apiClient.getDepartmentServices({
            department_id: this.departmentId,
            page:          this.currentPage,
            per_page:      this.itemsPerPage
          })
        }

        if (response.success) {
          this.services    = response.data
          this.currentPage = response.meta.current_page
          this.itemsPerPage = response.meta.per_page
          this.totalItems  = response.meta.total_items
          this.totalPages  = response.meta.total_pages
        } else {
          this.$toast.error("Failed to fetch services")
        }
      } catch (e) {
        this.$toast.error("Failed to fetch services")
      } finally {
        this.showLoader = false
      }
    },

    async fetchServiceTypes() {
      try {
        const response = await apiClient.getFacilityServiceTypes();
        if (response.success) {
          this.serviceTypes = response.data;
        }
      } catch (error) {
        this.$toast.error("Failed to fetch. Please check your internet connection")
        console.error("Failed to fetch service types", error);
      }
    },

    goBack() {
      if (this.discipline) {
        this.$router.push({
          name: 'LabDisciplines',
          params: { departmentId: this.departmentId, departmentName: this.departmentName }
        })
      } else {
        this.$router.push('/facility-services')
      }
    },

    goToPage(pageNumber) {
      this.currentPage = pageNumber;
      this.fetchServices();
    },

    changePageSize(pageSize) {
      this.itemsPerPage = pageSize;
      this.currentPage = 1;
      this.fetchServices();
    },

    triggerSearch() {
      if (!this.searchQuery.trim()) {
        this.fetchServices();
        return;
      }

      const params = {
        name: this.searchQuery,
        department_id: this.departmentId,
      };
      this.searchServices(params);
    },

    async searchServices(params) {
      try {
        this.showLoader = true;
        const response = await apiClient.searchFacilityServices(params);

        if (response.success) {
          this.services = response.data;
        }
      } catch (error) {
        this.$toast.error("Unable to search services");
      } finally {
        this.showLoader = false;
      }
    },

    handleEditModal(service) {
      this.selectedService = service;
      this.showEditFacilityServiceModal = true;
    },

    closeEditModal() {
      this.showEditFacilityServiceModal = false;
      this.selectedService = null;
    },

    confirmDelete(service) {
      this.itemToDelete = service;
      this.showDeleteConfirmationModal = true;
    },

    async deleteFacilityService() {
      try {
        this.showDeleteLoader = true;
        const response = await apiClient.deleteFacilityService(
          this.itemToDelete.id
        );

        if (response.success) {
          this.$toast.success("Service deleted successfully");
          this.fetchServices();
        } else {
          this.$toast.error("Failed to delete service");
        }
      } catch (error) {
        this.$toast.error("Failed to delete service");
        console.error(error);
      } finally {
        this.showDeleteLoader = false;
        this.showDeleteConfirmationModal = false;
      }
    },

    closeConfirmationModal() {
      this.showDeleteConfirmationModal = false;
      this.itemToDelete = null;
    },

    formatDate(dateString) {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      const date = new Date(dateString);
      return date.toLocaleString("en-US", options);
    },
  },

  mounted() {
    this.departmentId   = this.$route.params.departmentId
    this.departmentName = this.$route.params.departmentName
    this.discipline     = this.$route.params.discipline || null   // ← new

    if (!this.departmentId) {
      this.$toast.error("Department not found")
      this.$router.push("/facility-services")
      return
    }
    this.fetchServices()
    this.fetchServiceTypes()
  },
}
</script>

<style scoped>
.bg-custom-yellow {
  background-color: #f59e0b;
}

.bg-custom-blue {
  background-color: #3b82f6;
}
</style>